import type {
  CreateRewardPayload,
  LoyaltyRuleHistoryEntry,
  LoyaltyRulesUpdatePayload,
  LoyaltyRulesView,
  RewardRow,
  RewardView,
  UpdateRewardPayload,
} from "@/modules/loyalty/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/loyalty`;

export type ListRewardsParams = {
  isActive?: "true" | "false";
  search?: string;
  limit?: number;
  offset?: number;
  sortField?: "name" | "requiredPoints" | "createdAt";
  sortOrder?: "asc" | "desc";
};

export type ListRewardsResult = {
  data: RewardRow[];
  total: number;
};

export const loyaltyApi = {
  getRules: (): Promise<LoyaltyRulesView> =>
    fetcher.get<LoyaltyRulesView>(`${base}/rules`),

  updateRules: (payload: LoyaltyRulesUpdatePayload): Promise<LoyaltyRulesView> =>
    fetcher.put<LoyaltyRulesView>(`${base}/rules`, payload),

  getRuleHistory: (): Promise<LoyaltyRuleHistoryEntry[]> =>
    fetcher.get<LoyaltyRuleHistoryEntry[]>(`${base}/rules/history`),

  getRewardsSummary: (): Promise<{
    totalRewards: number;
    activeCount: number;
    avgRedemption: number;
  }> => fetcher.get(`${base}/rewards/summary`),

  listRewards: (params?: ListRewardsParams): Promise<ListRewardsResult> => {
    const search = new URLSearchParams();
    if (params?.isActive) search.set("isActive", params.isActive);
    if (params?.search) search.set("search", params.search);
    if (params?.limit != null) search.set("limit", String(params.limit));
    if (params?.offset != null) search.set("offset", String(params.offset));
    if (params?.sortField) search.set("sortField", params.sortField);
    if (params?.sortOrder) search.set("sortOrder", params.sortOrder);
    const q = search.toString();
    return fetcher.get<ListRewardsResult>(`${base}/rewards${q ? `?${q}` : ""}`);
  },

  getReward: (rewardId: string): Promise<RewardView | null> =>
    fetcher.get<RewardView | null>(`${base}/rewards/${rewardId}`),

  createReward: (payload: CreateRewardPayload): Promise<RewardView> =>
    fetcher.post<RewardView>(`${base}/rewards`, payload),

  updateReward: (
    rewardId: string,
    payload: UpdateRewardPayload,
  ): Promise<RewardView> =>
    fetcher.put<RewardView>(`${base}/rewards/${rewardId}`, payload),

  deactivateReward: (rewardId: string): Promise<RewardView> =>
    fetcher.patch<RewardView>(`${base}/rewards/${rewardId}/deactivate`),

  listVariantOptions: (
    search?: string,
    limit?: number,
  ): Promise<{ items: { id: string; displayName: string }[] }> => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (limit != null) params.set("limit", String(limit));
    const q = params.toString();
    return fetcher.get(`${base}/rewards/variant-options${q ? `?${q}` : ""}`);
  },

  /** Lookup สำหรับ FormInfiniteCombobox: ค้นหา + แบ่งหน้า */
  listVariantOptionsLookup: (params: {
    search?: string;
    page: number;
    limit?: number;
  }): Promise<{
    items: { id: string; displayName: string }[];
    nextPage: number | null;
  }> => {
    const search = new URLSearchParams();
    search.set("page", String(params.page));
    if (params.search) search.set("search", params.search);
    if (params.limit != null) search.set("limit", String(params.limit));
    return fetcher.get(
      `${base}/rewards/variant-options?${search.toString()}`,
    );
  },

  /** Preload variant option by id (สำหรับแสดง label ตอนมี value แล้ว) */
  getVariantOption: (
    id: string,
  ): Promise<{ id: string; displayName: string } | null> =>
    fetcher
      .get<{ id: string; displayName: string }>(
        `${base}/rewards/variant-options/${id}`,
      )
      .catch(() => null),
};
